import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { getMyVault } from '../lib/vault/vaultApi';
import {
  inviteGuardian,
  listGuardians,
  replaceGuardian,
  type Guardian,
} from '../lib/guardians/guardianApi';
import './guardians.css';

/**
 * Replace a guardian — the safe way to change someone who holds a piece of your
 * key. The person leaving keeps their piece until the new person has accepted
 * and holds one of their own, so your vault is never, even for a moment, left
 * with too few pieces to open. (Removing a share-holder at the floor would do
 * exactly that — which is why the vault refuses it and sends you here instead.)
 */
export default function GuardianReplaceScreen() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id: guardianId } = useParams<{ id: string }>();

  const [vaultId, setVaultId] = useState<string | null>(null);
  const [outgoing, setOutgoing] = useState<Guardian | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const dash = await getMyVault(api);
        if (cancelled) return;
        setVaultId(dash.vault.id);
        const res = await listGuardians(api, dash.vault.id);
        if (cancelled) return;
        setOutgoing(res.guardians.find((g) => g.id === guardianId) ?? null);
      } catch {
        if (!cancelled) setError('We could not open your vault just now.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, guardianId]);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (vaultId === null || guardianId === undefined) return;
    if (name.trim() === '' || email.trim() === '') {
      setError('We need their name and their email.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await replaceGuardian(api, vaultId, guardianId, {
        invited_name: name.trim(),
        invited_email: email.trim(),
      });
      // Tell the successor at once, so they don't sit unaware in a chain you're
      // relying on.
      await inviteGuardian(api, vaultId, res.successor.id);
      navigate('/guardians', { replace: true });
    } catch (err) {
      const status = (err as { status?: number }).status;
      setError(
        status === 409
          ? 'This guardian already has a replacement pending, or cannot be replaced right now.'
          : 'We could not name a replacement. Please try again.',
      );
      setBusy(false);
    }
  }

  return (
    <section className="guard">
      <p className="guard__kicker">A safe hand-over</p>
      <h1 className="guard__title">
        Replace {outgoing ? outgoing.invited_name : 'a guardian'}.
      </h1>
      <p className="guard__sub">
        Name someone to take over{outgoing ? ` from ${outgoing.invited_name}` : ''}. Nothing changes
        until the new person accepts and holds their own piece of your key — then you seal it to them,
        and only then does the person leaving stand down. Your vault is never left short.
      </p>

      <form onSubmit={(e) => void onSubmit(e)}>
        <label className="guard__field">
          <span className="guard__label">Their name</span>
          <input
            className="guard__input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </label>

        <label className="guard__field">
          <span className="guard__label">Their email</span>
          <input
            className="guard__input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>

        {error !== null && (
          <p role="alert" className="guard__alert">
            {error}
          </p>
        )}

        <div className="guard__actions">
          <button type="button" className="la-btn-secondary" onClick={() => navigate('/guardians')}>
            Back
          </button>
          <div className="la-cta-wrap">
            <button type="submit" className="la-btn-primary" disabled={busy || vaultId === null}>
              {busy ? 'Sending' : 'Name their replacement'}
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}
